// src/modules/ida/utils/messageUsage.ts
//
// Pure, display-only extractor that normalizes the backend-authored LLM
// usage/provenance carried on a gateway-migrated assistant message into the
// shape the per-message usage chip renders.
//
// SCOPE: gateway-provenance only. Reads, in precedence order:
//   (a) metadata._ida_llm_route_provenance (migrated gateway ops), then
//   (b) top-level {model, provider, prompt_tokens, completion_tokens,
//       total_tokens}.
// It does NOT read the legacy usage.agent_calls[] format (non-gateway BKG /
// Meridian ops are out of scope for now). The extractor never fabricates or
// derives values beyond summing the token subfields the backend already emits.

import type { IDAChatMessage } from "./chatMessageMapper";
import type {
  LlmRouteObservedUsage,
  LlmRouteProvenance,
} from "../components/llmRouteProvenanceTypes";

/** Normalized, display-ready usage for one assistant message. */
export interface NormalizedMessageUsage {
  /** Preferred human label: safe_display_name, else the raw model id. */
  modelLabel?: string;
  provider?: string;
  /** Route/operation identity: operation_key, else route_revision. */
  operation?: string;
  inputTokens?: number;
  outputTokens?: number;
  totalTokens?: number;
}

type UsageSource = Pick<
  IDAChatMessage,
  | "metadata"
  | "model"
  | "provider"
  | "prompt_tokens"
  | "completion_tokens"
  | "total_tokens"
>;

/** Sum the defined numeric parts; undefined when none are present. */
function sumDefined(...values: Array<number | null | undefined>): number | undefined {
  const present = values.filter(
    (value): value is number => typeof value === "number",
  );
  if (present.length === 0) return undefined;
  return present.reduce((total, value) => total + value, 0);
}

/**
 * Map the backend `observed.usage` block to input/output/total. The gateway
 * emits provider-specific subfields (uncached_input / cached_input_read on the
 * input side, visible_output / reasoning_output on the output side, plus a
 * pre-summed `total`). Read defensively; only display what is present.
 */
function tokensFromObservedUsage(usage: LlmRouteObservedUsage | null | undefined): {
  inputTokens?: number;
  outputTokens?: number;
  totalTokens?: number;
} {
  if (!usage) return {};
  const inputTokens = sumDefined(usage.uncached_input, usage.cached_input_read);
  const outputTokens = sumDefined(usage.visible_output, usage.reasoning_output);
  const totalTokens =
    typeof usage.total === "number"
      ? usage.total
      : sumDefined(inputTokens, outputTokens);
  return { inputTokens, outputTokens, totalTokens };
}

function usageFromProvenance(
  provenance: LlmRouteProvenance,
  source: UsageSource,
): NormalizedMessageUsage {
  const selected = provenance.selected_profile;
  const observed = provenance.observed;

  const modelLabel =
    selected?.safe_display_name ||
    selected?.model ||
    observed?.observed_model ||
    source.model;
  const provider =
    selected?.provider || observed?.model_identity?.provider || source.provider;
  const operation = provenance.operation_key || provenance.route_revision;

  // Prefer the canonical top-level prompt/completion counts when the message
  // carries them; otherwise fall back to the observed.usage subfields.
  const observedTokens = tokensFromObservedUsage(observed?.usage);
  const inputTokens = source.prompt_tokens ?? observedTokens.inputTokens;
  const outputTokens = source.completion_tokens ?? observedTokens.outputTokens;
  const totalTokens =
    source.total_tokens ??
    observedTokens.totalTokens ??
    sumDefined(inputTokens, outputTokens);

  return { modelLabel, provider, operation, inputTokens, outputTokens, totalTokens };
}

function usageFromTopLevel(source: UsageSource): NormalizedMessageUsage {
  const totalTokens =
    source.total_tokens ??
    sumDefined(source.prompt_tokens, source.completion_tokens);
  return {
    modelLabel: source.model,
    provider: source.provider,
    inputTokens: source.prompt_tokens,
    outputTokens: source.completion_tokens,
    totalTokens,
  };
}

/** True when the normalized usage carries at least one displayable field. */
function hasAnyField(usage: NormalizedMessageUsage): boolean {
  return (
    usage.modelLabel != null ||
    usage.provider != null ||
    usage.operation != null ||
    usage.inputTokens != null ||
    usage.outputTokens != null ||
    usage.totalTokens != null
  );
}

/**
 * Extract normalized, display-ready usage from an assistant message. Returns
 * null only when nothing is resolvable, so callers can keep a genuine
 * "No usage data" empty fallback.
 */
export function extractMessageUsage(
  message: UsageSource,
): NormalizedMessageUsage | null {
  const provenance = message.metadata?._ida_llm_route_provenance;
  if (provenance) {
    const usage = usageFromProvenance(provenance, message);
    if (hasAnyField(usage)) return usage;
  }

  const topLevel = usageFromTopLevel(message);
  if (hasAnyField(topLevel)) return topLevel;

  return null;
}
